import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";
import { SketchPicker } from "react-color";
import reactCSS from 'reactcss';

import {
    Card,
    Grid,
    FormControl,
    FormControlLabel,
    IconButton,
    ListItem,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import { AddCircleOutlined } from '@mui/icons-material';

import TrashIcon from '../../../icons/TrashIcon';

import apiClient from '../../../../services/api';

import store from '../../../../store';
import { toggleMessageOpen } from '../../../../store/actions/Message';
import { createConfirmAction } from '../../../../store/actions/ConfirmDialog';

import { LoadingScreen } from '../../../partials/LoadingScreen';

import StyledTextField from '../../../styled/TextField';
import StyledButton from '../../../styled/Button';

const ColorSearch = ({t, searchColors, filterColors, clearSelected}) => {
    return (
        <Fragment>
            <label htmlFor="search" style={{display: 'block', marginTop: '10px'}}>{t('Search color')}</label>
            <Grid container spacing={2} alignContent="center" mb={2}>
                <Grid item xs={9}>
                    <StyledTextField
                        value={searchColors}
                        onChange={filterColors}
                        id="search"
                        size="small"
                        fullWidth
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={3}>
                    <StyledButton
                        fullWidth
                        variant="contained"
                        startIcon={<AddCircleOutlined />}
                        onClick={clearSelected}
                    >
                        {t('Add color')}
                    </StyledButton>
                </Grid>
            </Grid>
        </Fragment>
    )
}

const ColorListContainer = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const ColorList = ({filteredColors, t}) => {
        return (
            filteredColors && filteredColors.length > 0 ? (
                <AutoSizer style={{ width: '100%' }} disableWidth>
                    {({ height }) => (
                        <FixedSizeList height={height - 140} itemSize={96} itemCount={filteredColors.length} overscanCount={8} style={{ overflow: 'auto', textOverflow: 'ellipsis'}}>
                            {props.renderColorRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            ) : (
                <Typography style={{padding: '1em', display: (filteredColors.length === 0 ? 'block' : 'none'), color: theme.palette.storeDrawer.tabs.label.color}}>
                    {t('No colors found!')}
                </Typography>
            )
        )
    }

    const ColorsDelete = ({handleSelectAll, allSelected, handleDelete}) => {
        return (
            <Grid container alignItems='center' alignContent='center' style={{marginBottom: '5px'}}>
                <Grid
                    item
                    xs={1.5}
                    style={{
                        display: 'flex',
                        justifyContent: 'start',
                        backgroundColor: theme.palette.background.tab,
                        paddingTop: '2px',
                        paddingBottom: '2px',
                        borderRadius: '8px'
                    }}
                >
                    <input
                        type='checkbox'
                        checked={allSelected}
                        style={{marginRight: '5px', marginLeft: '15px'}}
                        onChange={handleSelectAll}
                    />
                    <IconButton 
                        onClick={() => {
                            store.dispatch(createConfirmAction(handleDelete, t('Are you sure?'), 'selected'))
                        }}
                    >
                        <TrashIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={10.5}></Grid>
            </Grid>
        )
    }

    return (
        <Paper sx={{padding: '15px', height: '102%'}}>
            <Typography variant='h1'>{t('Colors')}</Typography>
            <ColorSearch
                t={t}
                searchColors={props.searchColors}
                filterColors={props.filterColors}
                clearSelected={props.clearSelected}
            />
            <ColorsDelete
                handleSelectAll={props.handleSelectAll}
                allSelected={props.allSelected}
                handleDelete={props.handleDelete}
            />
            <ColorList
                filteredColors={props.filteredColors}
                t={t}
            />
        </Paper>
    )
}

const ColorForm = (props) => {
    const FileInput = ({onChangeCallback, name}) => {
        return (<input type="file" id="icon" name={name} onChange={onChangeCallback} style={{marginLeft: '5px'}}/>)
    }

    const ColorPicker = ({color, styles, displayColorPicker, onChangeCallback, onClickCallback, onCloseCallback}) => {
        return (
            <div style={{marginLeft: '5px'}}>
                <div style={ styles.swatch } onClick={ onClickCallback }>
                    <div style={{backgroundColor: color, width: '36px', height: '14px', borderRadius: '2px'}} />
                </div>
    
                {
                    displayColorPicker
                        ?   <div style={ styles.popover }>
                                <div style={ styles.cover } onClick={ onCloseCallback }/>
                                <SketchPicker color={ color } onChange={ onChangeCallback } />
                            </div>
                        : null
                }
            </div>
        )
    }

    return (
        <Paper sx={{padding: '15px'}}>
            <Typography variant='h2' style={{marginBottom: '10px'}}>{props.t('Edit color information')}</Typography>
            <StyledTextField
                size="small"
                margin="dense"
                id="name"
                label={props.t("Name")}
                type="text"
                fullWidth
                variant="outlined"
                value={props.name}
                onChange={props.handleChangeName}
                error={props.errors.name}
                helperText={props.errors.name}
            />
            <FormControl style={{display: 'black'}}>
                <FormControlLabel
                    style={{margin: 0}}
                    value={props.visibleInLegend}
                    control={<ColorPicker
                        color={props.hex}
                        displayColorPicker={props.displayColorPicker}
                        styles={props.styles}
                        onClickCallback={props.handleColorPickerClick}
                        onChangeCallback={newColor => { props.setHex(newColor.hex) }}
                        onCloseCallback={props.handleColorPickerClose}
                    />}
                    label={props.t('Select color')+': '}
                    labelPlacement='start'/>
            </FormControl>
            <div style={{marginBottom: '10px'}}>
                <FormControl component='fieldset'>
                    <FormControlLabel
                        style={{marginLeft: 0}}
                        control={<FileInput name={props.icon} onChangeCallback={props.handleFileUpload} />}
                        label={props.t('Upload file')+': '}
                        labelPlacement='start'/>
                </FormControl>
            </div>
            <div style={{textAlign: 'center'}}>
                <img src={props.icon} width='250px' alt='icon'/>
            </div>
            <StyledButton
                variant="contained"
                style={{marginTop: '15px'}}
                onClick={props.handleSave}
            >
                {props.selectedColor ? props.t('Update') : props.t('Add')}
            </StyledButton>
        </Paper>
    )
}

function ColorsContainer() {
    const {t} = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const [selectedColor, setSelectedColor] = useState(null);
    const [allSelected, setAllSelected] = useState(false);
    const [colors, setColors] = useState([]);
    const [filteredColors, setFilteredColors] = useState([]);
    const [searchColors, setSearchColors] = useState('');
    const [errors, setErrors] = React.useState([]);

    const [name, setName] = useState('');
    const [hex, setHex] = useState({r: '0', g: '0', b: '0', a: '1'});
    const [icon, setIcon] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);    
    const [displayColorPicker, setDisplayColorPicker] = useState(false);
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
        setLoading(true);

        apiClient.post('/api/modules/check', {name: 'colorGroups'}).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(t("You don't have permission to enter this page"), "error"))

                navigate('/app/home');
            } else {
                setLoading(false)
            }
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps

    const handleChangeName = (event) => { setName(event.target.value) }

    const styles = reactCSS({
        'default': {
            swatch: {
                padding: '5px',
                background: '#fff',
                borderRadius: '1px',
                boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                display: 'inline-block',
                cursor: 'pointer',
            },
            popover: {
                position: 'absolute',
                zIndex: '2',
            },
            cover: {
                position: 'fixed',
                top: '0px',
                right: '0px',
                bottom: '0px',
                left: '0px',
            },
        },
    })

    useEffect(() => {
        if(selectedColor) {
            setName(selectedColor.name)
            setHex(selectedColor.hex)
            setIcon(selectedColor.icon)
        }
    }, [selectedColor])

    useEffect((navigate, t) => {
        apiClient.get('/api/colors').then(response => {
            response.data.colors.map(function(item) { item.selected = false; return item})

            setColors(response.data.colors);
            setFilteredColors( response.data.colors);
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t("Colors can't be loaded"), "error"));
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps
    
    const getData = () => {
        const formData = new FormData();

        formData.append('id', (selectedColor ? selectedColor.id : 0));
        formData.append('name', name);
        formData.append('hex', hex);

        if(uploadedFile)
        {
            formData.append('icon', uploadedFile);
        }

        if(!icon)
        {
            formData.append('icon', '');
        }
        
        return formData;
    }

    const handleFileUpload = (event) => {
        const [file] = event.target.files;

        if(file)
        {
            setIcon(URL.createObjectURL(file));
            setUploadedFile(file);
        }
    }

    const handleColorPickerClick = () => {
        setDisplayColorPicker(!displayColorPicker);
    }

    const handleColorPickerClose = () => {
        setDisplayColorPicker(false);
    }

    const handleDelete = (type, item) => {
        let ids;

        if(type === 'selected') {
            let selected = [];

            filteredColors.foreach(function(color, index) {
                if(filteredColors[index]['selected']) {
                    selected.push(color.id);
                }
            });

            ids = selected.join(',');
        } else {
            ids = item.id;
        }

        apiClient.post('/api/colors/delete', {ids: ids}).then(response => {
            if(response.data.success) {
                setColors(response.data.colors);
                setFilteredColors(response.data.colors);
                
                store.dispatch(toggleMessageOpen(t('Color deleted successfully'), "success"));
            }
        }).catch( error => store.dispatch(toggleMessageOpen(error, "error")) )
    }
    
    const handleSave = () => {
        const headers = {'Content-Type': 'multipart/form-data'};
        const data = getData();

        apiClient.post('/api/colors/save', data, {headers}).then(response => {
            if(response.data.success) {
                setColors(response.data.colors);
                setFilteredColors(response.data.colors);
                setUploadedFile(null);

                store.dispatch(toggleMessageOpen(t('Color group saved successfully!'), 'success'));
            } else {
                store.dispatch(toggleMessageOpen(response.data.message, 'error'));
            }
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t('An error occurred!'), 'error'));
        });
    }

    const renderColorRow = (rowProps) => {
        const { index, style } = rowProps;
      
        return (
            <ListItem
                style={style}
                sx={{marginBottom: '10px'}}
                key={`color`+index}
                component="div"
                disablePadding
                onClick={(event) => handleColorClicked(event, index)}
            >
                <Card 
                    sx={{
                        width: '100%',
                        backgroundColor: theme.palette.background.tab,
                        border: localStorage.getItem('currentTheme') !== 'Dark'
                            ? '1px solid ' + theme.palette.border.main
                            : 'none'
                    }}
                >
                    <Grid container alignItems='center' justifyItems='center' style={{height: '86px'}} spacing={1}>
                        <Grid item xs={1.5} style={{display: 'flex', justifyContent: 'start'}}>
                            <input 
                                type="checkbox"
                                checked={filteredColors[index]['selected']}
                                onChange={(event) => handelSelectedChange(event, index)}
                                onClick={(event) => {event.stopPropagation()}}
                                style={{marginLeft: '15px'}}
                            />
                        </Grid>
                        <Grid item xs={1} style={{display: 'flex', justifyContent: 'start'}}>
                            <div
                                style={{
                                    width: '25px',
                                    height: '25px',
                                    backgroundColor: filteredColors[index]['hex'],
                                    borderRadius: '5px',
                                    display: 'inline-block'
                                }}
                            >
                                &nbsp;
                            </div>
                        </Grid>
                        <Grid item xs={1} style={{display: 'flex', justifyContent: 'center'}}>
                            <img src={filteredColors[index]['icon']} style={{width: '60px'}} alt="icon" />
                        </Grid>
                        <Grid item xs={7.5}>
                            <div>{filteredColors[index]['name']}</div>
                            <div style={{color: theme.palette.text.secondary}}>{filteredColors[index]['hex']}</div>
                        </Grid>
                        <Grid item xs={1} style={{display: 'flex', justifyContent: 'end'}}>
                            <IconButton
                                onClick={(event) => {
                                    event.stopPropagation();
                                    store.dispatch(createConfirmAction(handleDelete, t('Are you sure?'), 'single', filteredColors[index]))
                                }}
                            >
                                <TrashIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Card>
            </ListItem>
        );
    }
    
    const filterColors = (event) => {
        const keyword = event.target.value;
  
        if(keyword !== '') {
            const results = colors.filter(
                color => {
                    const value = color.name;

                    return value.toLowerCase().includes(keyword.toLowerCase())
                }
            );
  
            setFilteredColors(results)
        } else {
            setFilteredColors(colors)
        }
  
        setSearchColors(keyword);
    }

    const clearSelected = () => {
        setName('');
        setHex('');
        setIcon('');

        setSelectedColor(null);
        setErrors([]);
    }

    const handleColorClicked = (event, index) => {
        const updateList = [...filteredColors];

        updateList.map(function(item, i) {
            if(index !== i) {
                item.selected = false; 
            } else {
                item.selected = true;
            }
            
            return item
        });

        setFilteredColors(updateList);
        setSelectedColor(filteredColors[index]);
        setErrors([]);
    }

    const handelSelectedChange = (event, index) => {
        const updateList = [...filteredColors];
        updateList[index]['selected'] = event.target.checked;

        let nrOfSelected = 0;

        updateList.forEach(function(item) {
            if(item.selected) {
                nrOfSelected++
            }
        })

        if(nrOfSelected === 0 || nrOfSelected > 1) {
            clearSelected()
        }

        setFilteredColors(updateList);
    }

    const handleSelectAll = (event) => {
        const checked = event.target.checked;
        const updateList = [...filteredColors];

        updateList.map(function(item, i) { item.selected = checked; return item });

        setFilteredColors(updateList);
        setAllSelected(checked);
    }

    const renderData = () => {
        return (
            <Grid container spacing={3} sx={{height: '100%'}}>
                <Grid item xs={6}>
                    <ColorListContainer
                        filteredColors={filteredColors}
                        searchColors={searchColors}
                        allSelected={allSelected}
                        filterColors={filterColors}
                        renderColorRow={renderColorRow}
                        clearSelected={clearSelected}
                        handleSelectAll={handleSelectAll}
                        handleDelete={handleDelete}
                    />
                </Grid>
                <Grid item xs={4}>
                    <ColorForm
                        name={name}
                        hex={hex}
                        icon={icon}
                        selectedColor={selectedColor}
                        errors={errors}
                        displayColorPicker={displayColorPicker}
                        handleColorPickerClick={handleColorPickerClick}
                        handleColorPickerClose={handleColorPickerClose}
                        handleFileUpload={handleFileUpload}
                        setName={setName}
                        setHex={setHex}
                        setIcon={setIcon}
                        handleChangeName={handleChangeName}
                        handleSave={handleSave}
                        styles={styles}
                        t={t}
                    />
                </Grid>
            </Grid>
        )
    }

    return (
        !loading ? renderData() : <LoadingScreen />
    );
}

export default ColorsContainer;